import { api } from "../../lib/api.js";

// One slide of a social deck, as the server renders it: an <iframe> onto
// `/api/social/slide`, the SAME document the PNG exporter screenshots. Shared by
// the pack panel and the calendar's post dialog — a second renderer is how the
// preview and the published post drift apart.

/**
 * One slide, scaled down. The document is 1080 wide; it is transform-scaled
 * rather than reflowed, so the preview is a true miniature of the export.
 *
 * AT ITS OWN ASPECT. This rendered everything at 1080x1350 regardless of what
 * the variant actually posts, so a story — which exports at 1080x1920 — was
 * being previewed and TUNED as a portrait. Every element sits at a different
 * height on a 9:16 frame, and the logo size an operator settled on in the
 * preview was not the one that reached Instagram.
 */
export const SLIDE_DIMS: Record<string, { w: number; h: number }> = {
  portrait: { w: 1080, h: 1350 },
  square: { w: 1080, h: 1080 },
  story: { w: 1080, h: 1920 },
};

export function SlideThumb({ variantId, index, scale, width, nonce, format }: {
  variantId: number; index: number; scale: Record<string, number | string>;
  width: number; nonce: number; format: string;
}) {
  const dims = SLIDE_DIMS[format] ?? SLIDE_DIMS.portrait!;
  const H = Math.round((width * dims.h) / dims.w);
  return (
    // Neutral, not black: a slide that has not painted yet sits on this, and a
    // black rectangle reads as a broken render rather than a pending one.
    <div className="relative shrink-0 overflow-hidden rounded-lg border border-line bg-canvas" style={{ width, height: H }}>
      <iframe
        title={`slide-${index}`}
        src={`${api.socialSlideUrl({ variantId, index, scale, format })}&n=${nonce}`}
        scrolling="no"
        /**
         * Lazy, and it matters. Each slide is a full 1080x1350 document that
         * pulls a Sanity image and a webfont; a pack with an Instagram carousel
         * AND a TikTok photo post is 18 of them. Rendering all of them eagerly
         * saturated the renderer badly enough that the panel stopped responding
         * to scrolling. Only the slides actually on screen are built.
         */
        loading="lazy"
        /* PINNED to the top-left, and the box is a positioning context.
           The iframe's layout box is 1080px wide inside a ~190px window,
           and in an RTL document an oversized box anchors to the RIGHT —
           which put its left edge, the transform origin, 104px outside
           the window. The document was loading and styling perfectly and
           painting entirely off-screen. Absolute placement makes this
           independent of writing direction instead of accidentally
           correct in one of them. */
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: dims.w,
          height: dims.h,
          border: 0,
          transform: `scale(${width / dims.w})`,
          transformOrigin: "top left",
        }}
      />
    </div>
  );
}

